import { shapes } from '@joint/plus';
import React, { ChangeEvent, ReactElement, useCallback, useEffect, useRef, useState } from 'react';

import Input from '../Input/Input';
import SmartAutocomplete from '../Input/SmartAutocomplete/SmartAutocomplete';
import { useBaseInspector } from './useBaseInspector';

import { Box, Chip, Divider, Stack, Switch, Tooltip, Typography } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ReactQuill from 'react-quill';
import * as LLMService from "../../../../../../../../services/llm.service";
import HelperCustomField from '../../HelperCustomField/HelperCustomField';
import { da } from 'date-fns/locale';

// Cores canonicas por origem — precisam bater com o accordion do CRUD LLM (F5.5).
const SOURCE_USER = 1;
const SOURCE_LLM = 2;
const COLOR_USER = '#1976d2';   // azul
const COLOR_LLM  = '#8e24aa';   // roxo

interface BaeList {
    id: number;
    name: string;
    source?: number;
    suppressLlmMessage?: boolean;
}

interface Props {
    cell: shapes.app.LLM;
    chatbotFlowId: string;
}

interface InspectorPort {
    id: string;
    label: string;
    eventId: number;
    source?: number;
    suppressLlmMessage?: boolean;
}

const cellProps = {
    label: ['attrs', 'label', 'text'],
    description: ['attrs', 'description', 'text'],
    continuationMessage: ['attrs', 'continuationMessage', 'text'],
    displayText: ['attrs', 'displayText', 'text'],
    icon: ['attrs', 'icon', 'xlinkHref'],
    portLabel: ['attrs', 'portLabel', 'text'],
    eventId: ['attrs', 'eventId'],
    llmId: ['attrs', 'llmId', 'id'],
    isTextRich: ['attrs', 'isTextRich', 'isTextRich'],
    flexibility: ['attrs', 'flexibility', 'value'],
};

const LLMInspector = (props: Props): ReactElement => {

    const { cell, chatbotFlowId } = props;

    const [llmOptions, setLLMOptions] = useState([]);

    const [label, setLabel] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [continuationMessage, setContinuationMessage] = useState<string>('');
    const [llmId, setLLMId] = useState(null);
    const [isTextRich, setIsTextRich] = useState<boolean>(false);
    const [displayText, setDisplayText] = useState<string>(''); 
    const [flexibility, setFlexibility] = useState('static');
    const [isDisplayTextOnFocus, setIsDisplayTextOnFocus] = useState<boolean>(false);

    // Map eventId → {source, suppressLlmMessage} carregado do BE.
    // Usado pra enriquecer portas com metadata (cor por origem, chip "silent").
    const [eventMetadata, setEventMetadata] = useState<Record<number, { source: number; suppressLlmMessage: boolean }>>({});

    const [ports, setPorts] = useState<InspectorPort[]>(cell.getGroupPorts('out').map(({ id }) => {
            return {
                id,
                label: cell.portProp(id, cellProps.portLabel),
                eventId: cell.portProp(id, cellProps.eventId),
            };
        }));

    const [canAddPort, setCanAddPort] = useState<boolean>(false);

    const assignFormFields = useCallback((): void => {
        setLabel(cell.prop(cellProps.label));
        setDescription(cell.prop(cellProps.description));
        setLLMId(cell.prop(cellProps.llmId));
        setIsTextRich(cell.prop(cellProps.isTextRich));
        setDisplayText(cell.prop(cellProps.displayText));
        setFlexibility(cell.prop(cellProps.flexibility));
        setContinuationMessage(cell.prop(cellProps.continuationMessage));
    }, [cell]);

    const assignFormPorts = useCallback((): void => {
        setCanAddPort(cell.canAddPort('out'));
        setPorts(cell.getGroupPorts('out').map(({ id }) => {
            return {
                id,
                label: cell.portProp(id, cellProps.portLabel),
                eventId: cell.portProp(id, cellProps.eventId),
            };
        }));
    }, [cell]);

    const changeCellProp = useBaseInspector({ cell, assignFormFields });

    const addCellPort = (name?: string, eventId?: number, source?: number): void => {
        // F6.D: repassa `source` (1=UserMessage, 2=LlmResponse) pro shape
        // colorir a porta SVG (azul/roxo).
        (cell as any).addDefaultPort(name, eventId, source ?? null);
        assignFormPorts();
    };

    const cleanupPorts = () => {
        const portsToRemove = ports.filter(port => port.eventId && port.eventId !== 0);
        if (portsToRemove.length > 0) {
            cell.removePorts(portsToRemove);
            assignFormPorts();
        }
    };

    const fetchLLMs = async () => {
        try {
            const output = await LLMService.getLLM();

            if (output?.success) {
                setLLMOptions(output?.data ?? []);
            } else {
                setLLMOptions([]);
            }
        } catch (err) {
            setLLMOptions([]);
            console.error(err);
        }
    }

    const fetchListEventDynamicByLLM = async () => {
        try {
            const output = await LLMService.getListEventDynamicsByLLM(llmId);

            if (output?.success) {
                const events: BaeList[] = output?.data ?? [];
                // Popula o metadata cache com source + suppress pra usar na UI de portas.
                const meta: Record<number, { source: number; suppressLlmMessage: boolean }> = {};
                events.forEach(ev => {
                    if (ev.id) {
                        meta[ev.id] = {
                            source: Number(ev.source ?? SOURCE_USER),
                            suppressLlmMessage: !!ev.suppressLlmMessage,
                        };
                    }
                });
                setEventMetadata(prev => ({ ...prev, ...meta }));
                return events;
            } else {
                return [];
            }
        } catch (err) {
            console.error(err);
            return [];
        }
    }

    const loadComponent = async () => {
        await fetchLLMs();
    }

    const loadEventsPort = async () => {
        if (isNaN(llmId)) {
            cleanupPorts();
            return;
        }
        const events = await fetchListEventDynamicByLLM() as BaeList[];

        // F6 fix (rodada 4): sincronizar portas com a LLM SELECIONADA AGORA.
        // Fluxo:
        //   1) Remove portas cujo eventId NAO esta mais nos eventos da LLM atual
        //      (bug anterior: portas da LLM anterior ficavam presas no cell).
        //   2) Adiciona portas de eventos novos que ainda nao existem.
        //   3) Reconciliia cor (fill) das portas remanescentes — pois pode ser
        //      mesmo eventId com source diferente (ex.: admin trocou UserMsg→LlmResp
        //      no CRUD LLM). O useEffect de reconciliation ja cobre isso, mas
        //      chamamos assignFormPorts pra forcar re-render da lista visual.
        const validEventIds = new Set(events.map(e => e.id));

        const currentOutPorts: any[] = cell.getGroupPorts('out');
        const portsToRemove = currentOutPorts.filter(p => {
            const evId = cell.portProp(p.id, cellProps.eventId);
            return evId && !validEventIds.has(evId);
        });
        if (portsToRemove.length > 0) {
            cell.removePorts(portsToRemove);
        }

        // 2) Adiciona os novos
        const remainingPorts: any[] = cell.getGroupPorts('out');
        const remainingEventIds = new Set(
            remainingPorts.map(p => cell.portProp(p.id, cellProps.eventId)).filter(Boolean)
        );
        const newEvents = events.filter(ev => ev?.id && !remainingEventIds.has(ev.id));
        newEvents.forEach(event => {
            if (event?.id && event?.name) {
                addCellPort(event.name, event.id, Number(event.source ?? SOURCE_USER));
            }
        });

        // 3) Refresh state
        assignFormPorts();
    }

    // F6.D reconciliation: portas JA existentes no cell (carregadas do JSON do
    // fluxo) podem estar sem cor porque foram criadas antes do source existir.
    // Sempre que o metadata chegar, sincronizamos a cor de cada porta pelo
    // eventId → source lookup. Idempotente (setar mesmo fill nao dispara evento).
    useEffect(() => {
        const outPorts = cell.getGroupPorts('out');
        outPorts.forEach((p: any) => {
            const eventId = p?.attrs?.eventId;
            if (!eventId) return;
            const src = Number(eventMetadata[eventId]?.source ?? SOURCE_USER);
            const fill = src === SOURCE_LLM ? COLOR_LLM : COLOR_USER;
            const current = cell.portProp(p.id, ['attrs', 'portBody', 'fill']);
            if (current !== fill) {
                cell.portProp(p.id, ['attrs', 'portBody', 'fill'], fill);
            }
        });
    }, [eventMetadata, cell]);

    useEffect(() => {
        loadComponent();
    }, [])

    useEffect(() => {
        if (llmId) {
            loadEventsPort();
        }
    }, [llmId])

    return (
        <>
            <h1>Componente selecionado</h1>

            <label>Label
                <Input type="text"
                       placeholder="Enter label"
                       value={label}
                       disabled={true}
                />
            </label>

            <div style={{ marginBottom: '8px', marginTop: '8px'}}>
                <Switch
                    value={isTextRich}
                    checked={isTextRich}
                    onChange={(e:any) => changeCellProp(cellProps.isTextRich, e?.target?.checked)}     
                />
                <label>É um texto rico?</label>
            </div>

            {
                isTextRich
                ? (
                    <label>Descrição
                        <div style={{ resize: "vertical", overflow: "auto", height: "250px", marginBottom: "24px", border: "1px solid #ccc" }}>
                            <ReactQuill
                                name="description"
                                style={{ height: '100%' }}
                                fullWidth
                                variant="outlined"
                                id="descriprion"
                                value={description}
                                onFocus={(e) => setIsDisplayTextOnFocus(false)}
                                onChange={(value: any) => changeCellProp(cellProps.description, value)}
                            />
                        </div>
                    </label>
                )
                : (
                    <label>Descrição
                        <Input 
                            type="text"
                            placeholder="Descrição"
                            value={description}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => changeCellProp(cellProps.description, e.target.value)}
                            onSetIsDisplayTextOnFocus={setIsDisplayTextOnFocus}
                        />
                    </label>
                )
            }

            <label>Mensagem de continuação da conversa
                <Input 
                    type="text"
                    placeholder="Mensagem"
                    value={continuationMessage}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => changeCellProp(cellProps.continuationMessage, e.target.value)}
                    onSetIsDisplayTextOnFocus={setIsDisplayTextOnFocus}
                />
            </label>

            <HelperCustomField
                isRichText={isDisplayTextOnFocus ? true : isTextRich}
                description={isDisplayTextOnFocus ? displayText : description}
                param1={isDisplayTextOnFocus ? cellProps.displayText : cellProps.description}
                onChangeDescription={changeCellProp}
                chatbotFlowId={chatbotFlowId}
            />

            <label>LLM
                <SmartAutocomplete 
                    value={llmId}
                    onChange={(e) => changeCellProp(cellProps.llmId, e.target.value)}
                    options={llmOptions}
                />
            </label>

            {/* ─── Portas de eventos dinamicos: lista + legenda de origem (F5.5) ───
                Cada porta out do LLM box corresponde a um evento dinamico
                cadastrado no LLM selecionado. Aqui mostramos chips coloridos por
                origem (azul = mensagem usuario, roxo = resposta LLM) pra dar
                visibilidade imediata de qual conexao dispara em qual momento. */}
            {ports.filter(p => p.eventId).length > 0 && (
                <Box sx={{ mt: 2, p: 1.5, border: '1px solid rgba(0,0,0,0.1)', borderRadius: 1, background: '#fafafa' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Eventos dinâmicos ({ports.filter(p => p.eventId).length})
                    </Typography>
                    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                        {ports.filter(p => p.eventId).map(port => {
                            const meta = eventMetadata[port.eventId];
                            const src = Number(meta?.source ?? SOURCE_USER);
                            const isLlm = src === SOURCE_LLM;
                            const color = isLlm ? COLOR_LLM : COLOR_USER;
                            const label = port.label + (meta?.suppressLlmMessage ? ' · silent' : '');
                            return (
                                <Tooltip
                                    key={port.id}
                                    title={isLlm ? 'Dispara pela resposta da LLM' : 'Dispara pela mensagem do usuário'}
                                >
                                    <Chip
                                        icon={isLlm ? <SmartToyIcon /> : <PersonIcon />}
                                        label={label}
                                        size="small"
                                        sx={{
                                            backgroundColor: `${color}22`,
                                            color,
                                            fontWeight: 500,
                                            '& .MuiChip-icon': { color },
                                        }}
                                    />
                                </Tooltip>
                            );
                        })}
                    </Stack>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                        Legenda:
                    </Typography>
                    <Stack direction="row" spacing={1}>
                        <Chip
                            icon={<PersonIcon />}
                            label="Mensagem do usuário"
                            size="small"
                            sx={{ backgroundColor: `${COLOR_USER}22`, color: COLOR_USER, '& .MuiChip-icon': { color: COLOR_USER } }}
                        />
                        <Chip
                            icon={<SmartToyIcon />}
                            label="Resposta da IA"
                            size="small"
                            sx={{ backgroundColor: `${COLOR_LLM}22`, color: COLOR_LLM, '& .MuiChip-icon': { color: COLOR_LLM } }}
                        />
                    </Stack>
                </Box>
            )}
        </>
    );
};

export default LLMInspector;
